import {
  FileText,
  Globe,
  Layers,
  LayoutDashboard,
  Settings,
  Users,
} from 'lucide-react';
import { useEffect } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { LiksPagination } from '../../components/liks/LiksPagination/LiksPagination';
import { LiksLucideIcon } from '../../components/liks/LiksLucideIcon/LiksLucideIcon';
import { LiksPage } from '../../components/liks/LiksPage/LiksPage';
import { LiksPageHeading } from '../../components/liks/LiksPageHeading/LiksPageHeading';
import { LiksTabs } from '../../components/liks/LiksTabs/LiksTabs';
import { useI18n } from '../../i18n/useI18n';
import { useCurrentKnowledgeDatabaseRole } from '../../features/knowledge/hooks/useCurrentKnowledgeDatabaseRole';
import { useKnowledgeDocumentsTab } from '../../features/knowledge/hooks/useKnowledgeDocumentsTab';
import {
  canDeleteKdb,
  canEditKdb,
  canManageKdbCrawls,
  canManageKdbDocuments,
} from '../../features/knowledge/knowledgeDatabasePermissions';
import {
  DEFAULT_KNOWLEDGE_DATABASE_TAB,
  isKnowledgeDatabaseTabId,
  resolveKnowledgeDatabaseTabPath,
  type KnowledgeDatabaseTabId,
} from '../../features/knowledge/knowledgeTabs';
import { useKnowledgeDatabaseQuery } from '../../features/knowledge/queries/useKnowledgeDatabaseQuery';
import { useKnowledgeMembersQuery } from '../../features/knowledge/queries/useKnowledgeMembersQuery';
import { resetKnowledgeDatabaseDetailsStores } from '../../features/knowledge/stores/resetKnowledgeDatabaseDetailsStores';
import { KnowledgeDatabaseMembersPanel } from './tabs/KnowledgeDatabaseMembersPanel';
import { KnowledgeCrawlsTab } from './tabs/KnowledgeCrawlsTab';
import { KnowledgeDocumentsTab } from './tabs/KnowledgeDocumentsTab';
import { KnowledgeOverviewTab } from './tabs/KnowledgeOverviewTab';
import { KnowledgeSettingsTab } from './tabs/KnowledgeSettingsTab';
import { normalizeLucideIconName } from '../../utils/lucideIconCatalog';
import { DOCUMENT_PAGE_SIZES } from '../../types/userConfig';
import '../../styles/resourceStatus.css';
import './KnowledgeDatabasePage.css';

function isKnowledgeDocumentsTabId(
  tabId: KnowledgeDatabaseTabId | null,
): tabId is 'documents' | 'staging-documents' {
  return tabId === 'documents' || tabId === 'staging-documents';
}

export function KnowledgeDatabasePage() {
  const { databaseId, tab } = useParams<{
    databaseId: string;
    tab?: string;
  }>();
  const navigate = useNavigate();
  const { translate } = useI18n();

  const databaseQuery = useKnowledgeDatabaseQuery(databaseId);
  const membersQuery = useKnowledgeMembersQuery(databaseId);
  const currentKdbRole = useCurrentKnowledgeDatabaseRole(databaseId);
  const canEditDatabase = canEditKdb(currentKdbRole);
  const canDeleteDatabase = canDeleteKdb(currentKdbRole);
  const canManageDocuments = canManageKdbDocuments(currentKdbRole);
  const canManageCrawls = canManageKdbCrawls(currentKdbRole);
  const resolvedDatabaseId = databaseId?.trim() ?? '';
  const activeTabId = isKnowledgeDatabaseTabId(tab) ? tab : null;
  const isDocumentsListTab = isKnowledgeDocumentsTabId(activeTabId);
  const publishState =
    activeTabId === 'staging-documents' ? 'STAGING' : 'PUBLISHED';
  const documentsTabDocumentCount =
    activeTabId === 'staging-documents'
      ? (databaseQuery.data?.stagingDocumentCount ?? 0)
      : (databaseQuery.data?.documentCount ?? 0);
  const documentsTab = useKnowledgeDocumentsTab({
    databaseId: resolvedDatabaseId,
    canManageDocuments,
    documentCount: documentsTabDocumentCount,
    publishState,
    enabled: isDocumentsListTab && resolvedDatabaseId !== '',
  });

  useEffect(() => {
    resetKnowledgeDatabaseDetailsStores();
  }, [databaseId]);

  if (!databaseId) {
    return <Navigate to="/knowledge" replace />;
  }

  if (!isKnowledgeDatabaseTabId(tab)) {
    return (
      <Navigate
        to={resolveKnowledgeDatabaseTabPath(
          databaseId,
          DEFAULT_KNOWLEDGE_DATABASE_TAB,
        )}
        replace
      />
    );
  }

  const currentTabId: KnowledgeDatabaseTabId = tab;

  if (databaseQuery.isLoading || membersQuery.isLoading) {
    return (
      <LiksPage content={<p>{translate('knowledge.databaseLoading')}</p>} />
    );
  }

  if (currentTabId === 'settings' && !canEditDatabase) {
    return (
      <Navigate
        to={resolveKnowledgeDatabaseTabPath(databaseId, 'documents')}
        replace
      />
    );
  }

  if (databaseQuery.isError) {
    return (
      <LiksPage
        content={
          <p role="alert">
            {databaseQuery.error instanceof Error
              ? databaseQuery.error.message
              : translate('knowledge.databaseLoadError')}
          </p>
        }
      />
    );
  }

  const database = databaseQuery.data;
  if (!database) {
    return <Navigate to="/knowledge" replace />;
  }

  const members = membersQuery.data ?? [];

  const handleTabChange = (nextTabId: string) => {
    if (!isKnowledgeDatabaseTabId(nextTabId)) {
      return;
    }
    if (nextTabId === 'settings' && !canEditDatabase) {
      return;
    }
    navigate(resolveKnowledgeDatabaseTabPath(databaseId, nextTabId));
  };

  const tabs = [
    {
      id: 'overview',
      label: translate('knowledge.tabs.overview'),
      icon: <LayoutDashboard aria-hidden />,
      content: <KnowledgeOverviewTab database={database} />,
    },
    {
      id: 'documents',
      label: `${translate('knowledge.tabs.documents')} (${database.documentCount ?? 0})`,
      icon: <FileText aria-hidden />,
      content: <KnowledgeDocumentsTab documentsTab={documentsTab} />,
    },
    {
      id: 'staging-documents',
      label: `${translate('knowledge.tabs.stagingDocuments')} (${database.stagingDocumentCount ?? 0})`,
      icon: <Layers aria-hidden />,
      content: <KnowledgeDocumentsTab documentsTab={documentsTab} />,
    },
    {
      id: 'crawls',
      label: translate('knowledge.tabs.crawls'),
      icon: <Globe aria-hidden />,
      content: (
        <KnowledgeCrawlsTab
          databaseId={databaseId}
          canManageCrawls={canManageCrawls}
        />
      ),
    },
    {
      id: 'members',
      label: translate('knowledge.tabs.members'),
      icon: <Users aria-hidden />,
      content: (
        <KnowledgeDatabaseMembersPanel
          databaseId={databaseId}
          databaseName={database.name}
          members={members}
        />
      ),
    },
    ...(canEditDatabase
      ? [
          {
            id: 'settings',
            label: translate('knowledge.tabs.settings'),
            icon: <Settings aria-hidden />,
            content: (
              <KnowledgeSettingsTab
                databaseId={databaseId}
                database={database}
                canDeleteDatabase={canDeleteDatabase}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <LiksPage
      className={[
        'knowledge-database-page',
        isDocumentsListTab && 'knowledge-database-page--documents-table-scroll',
      ]
        .filter(Boolean)
        .join(' ')}
      header={
        <LiksPageHeading
          aria-label={translate('knowledge.breadcrumb.legend')}
          items={[
            { label: translate('knowledge.title'), to: '/knowledge' },
            {
              label: database.name,
              icon: (
                <LiksLucideIcon
                  name={normalizeLucideIconName(database.icon)}
                  strokeWidth={2}
                  aria-hidden
                />
              ),
            },
          ]}
        />
      }
      content={
        <div className="knowledge-database-page__content">
          <LiksTabs
            tabs={tabs}
            activeTabId={currentTabId}
            onActiveTabIdChange={handleTabChange}
            aria-label={translate('knowledge.tabs.legend')}
          />
        </div>
      }
      footer={
        isDocumentsListTab ? (
          <div className="knowledge-database-page__footer">
            <LiksPagination
              page={documentsTab.documentPage}
              totalPages={documentsTab.totalDocumentPages}
              pageSize={documentsTab.knowledgeDocumentsPageSize}
              pageSizes={DOCUMENT_PAGE_SIZES}
              onPageSizeChange={(size) =>
                documentsTab.handleDocumentPageSizeChange(String(size))
              }
              hasPreviousPage={documentsTab.hasPreviousDocumentPage}
              hasNextPage={documentsTab.hasNextDocumentPage}
              onPreviousPage={() =>
                documentsTab.setDocumentPage(
                  Math.max(1, documentsTab.documentPage - 1),
                )
              }
              onNextPage={() => {
                if (documentsTab.hasNextDocumentPage) {
                  documentsTab.setDocumentPage(documentsTab.documentPage + 1);
                }
              }}
              showBoundaries
              canGoToFirstPage={documentsTab.hasPreviousDocumentPage}
              canGoToLastPage={documentsTab.canJumpToLastDocumentPage}
              onFirstPage={() => documentsTab.setDocumentPage(1)}
              onLastPage={() => {
                if (documentsTab.totalDocumentPages != null) {
                  documentsTab.setDocumentPage(documentsTab.totalDocumentPages);
                }
              }}
            />
          </div>
        ) : undefined
      }
    />
  );
}
